import { Button } from 'primereact/button';
import { useIntl } from 'react-intl';
import { OnboardingTrialStatus } from '../../../hooks/coach/useOnboardingChecklist';
import { FREE_TIER_MODE } from '../../../config/featureFlags';

export interface TrialBannerProps {
  trial: OnboardingTrialStatus;
  /** CTA de upgrade. Mostrará Mercado Pago en Fase 4 — por ahora un placeholder. */
  onUpgradeClick?: () => void;
}

/**
 * Banner persistente sobre el estado del trial del coach. Tres estados:
 * - Activo y > 3 días: banner sutil informativo.
 * - Por vencer (≤ 3 días): banner amber con CTA fuerte.
 * - Vencido sin suscripción activa: banner rojo + CTA Mercado Pago.
 *
 * Si el coach tiene `hasActiveSubscription = true`, no se renderiza.
 * Ver `docs/coach-onboarding/06-trial-and-mercadopago.md`.
 */
export function TrialBanner({ trial, onUpgradeClick }: TrialBannerProps) {
  const intl = useIntl();

  // #38: mientras TrainEase no cobre no se muestra ningún aviso de
  // vencimiento. Ver el porqué en config/featureFlags.ts.
  if (FREE_TIER_MODE) return null;

  if (trial.hasActiveSubscription) return null;
  // Coaches pre-Fase 2 no tienen `trialEndsAt` poblado. No mostrar el banner
  // para no asustarlos con un "termina en 0 días" sin sentido.
  if (!trial.trialEndsAt && !trial.trialExpired) return null;

  const days = trial.daysRemaining ?? 0;
  const expired = trial.trialExpired || days < 0;
  const aboutToExpire = !expired && days <= 3;

  if (!expired && !aboutToExpire && days > 0) {
    return (
      <section
        data-testid="trial-banner-info"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.6rem 0.9rem',
          marginBottom: '0.75rem',
          borderRadius: 'var(--ios-radius-md, 12px)',
          background: 'rgba(163, 230, 53, 0.18)',
          color: '#4338ca',
          fontSize: '0.85rem'
        }}
      >
        <i className="pi pi-info-circle" />
        <span style={{ flex: 1 }}>
          {intl.formatMessage(
            {
              id: 'coach.trialBanner.active',
              defaultMessage: 'Estás en prueba gratuita. Te quedan {days} días.'
            },
            { days }
          )}
        </span>
      </section>
    );
  }

  // about-to-expire o expired
  const palette = expired
    ? { bg: 'rgba(239,68,68,0.1)', color: '#b91c1c', icon: 'pi pi-exclamation-triangle' }
    : { bg: 'rgba(245,158,11,0.12)', color: '#b45309', icon: 'pi pi-clock' };

  return (
    <section
      data-testid={expired ? 'trial-banner-expired' : 'trial-banner-warning'}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.85rem 1rem',
        marginBottom: '0.75rem',
        borderRadius: 'var(--ios-radius-md, 12px)',
        background: palette.bg,
        color: palette.color,
        fontWeight: 600
      }}
    >
      <i className={palette.icon} style={{ fontSize: '1.1rem' }} />
      <span style={{ flex: 1, fontSize: '0.9rem' }}>
        {expired
          ? intl.formatMessage({
              id: 'coach.trialBanner.expired',
              defaultMessage:
                'Tu prueba gratuita venció. Suscribite para invitar más alumnos y crear nuevas plantillas.'
            })
          : intl.formatMessage(
              {
                id: 'coach.trialBanner.warning',
                defaultMessage: 'Tu prueba gratuita termina en {days} {days, plural, one {día} other {días}}.'
              },
              { days }
            )}
      </span>
      {onUpgradeClick && (
        <Button
          label={intl.formatMessage({
            id: 'coach.trialBanner.cta',
            defaultMessage: 'Suscribirme'
          })}
          className="p-button-sm"
          onClick={onUpgradeClick}
          style={{
            background: '#a3e635',
            border: 'none',
            color: '#0a0a0a',
            padding: '0.4rem 0.9rem',
            borderRadius: 8,
            fontWeight: 600
          }}
        />
      )}
    </section>
  );
}
